import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  useEffect,
  ReactNode,
} from "react";
import Dexie from "dexie";
import { useLiveQuery } from "dexie-react-hooks";
import { toast } from "sonner";
import i18n, { detectBrowserLang, type SupportedUiLang } from "./i18n";
import type {
  AgeGroupId,
  CefrLevelId,
  EntityTypeId,
  LanguageId,
  LocaleId,
  TopicId,
} from "./lib/references";
import { loadStarterDeckCards, type StarterDeck } from "./lib/starterDecks";
import {
  db,
  cardStateKey,
  formKey,
  makeDirection,
  parseDirection,
  type CardState,
  type Concept,
  type ConceptForm,
  type DeckRow,
  type Direction,
} from "./lib/db";
import {
  shouldRemindBackup,
  KV_LAST_EXPORT_AT,
  KV_LAST_REMINDER_AT,
} from "./lib/backup";
import { migrateFromLocalStorageIfNeeded } from "./lib/migrateFromLocalStorage";
import { adoptOrphanPacks, localizedPackName } from "./lib/packs";
import { gradeCardState } from "./lib/study";
import { ensureDefaultWordsDeck } from "./lib/words";
import {
  enqueue,
  enqueueInTx,
  quiesceSync,
  scheduleFlush,
} from "./lib/sync/engine";
import { supabase } from "./lib/supabase";
import { DEMO_SEEDED_KEY } from "./lib/demoSeed";
import { resolvesToDark, type ThemeMode } from "./lib/theme";
import { migrateStoredMetaState } from "./lib/metaStateMigration";
import { getPlatformAdapter } from "./lib/platform";

/**
 * Optional classification fields shared by cards and decks. They map onto the
 * reference catalogs in `lib/references/*` and are kept optional so existing
 * decks created before classification was introduced remain valid.
 */
export type Classification = {
  topic?: TopicId;
  ageGroup?: AgeGroupId;
  level?: CefrLevelId;
};

/**
 * Adapter shape exposed to the rest of the UI. The underlying storage is now
 * concept-based (Dexie: `concepts` + `conceptForms` + `cardStates`); this
 * type flattens one (concept, direction) pair into the legacy front/back
 * pair the existing pages were built against.
 *
 * `id` is the cardState primary key (`${conceptId}|${direction}`) so callers
 * can still use it for keying lists and grading.
 */
export type Card = {
  id: string;
  deckId: string;
  sourceLang: LanguageId;
  targetLang: LanguageId;
  front: string;
  frontAliases: string[];
  back: string;
  backAliases: string[];
  nextReview: number;
  interval: number;
  repetition: number;
  efactor: number;
  entityType?: EntityTypeId;
  frequency?: number;
  classification?: Classification;
};

export type Deck = {
  id: string;
  name: string;
  createdAt: number;
  sourceLang?: LanguageId;
  targetLang?: LanguageId;
  classification?: Classification;
};

export type UserProfile = {
  name: string;
  avatar?: string;
  joinedAt: number;
};

export type { ThemeMode } from "./lib/theme";

export type UserSettings = {
  role: "student" | "tutor";
  cloudLinked: boolean;
  onboardingCompleted: boolean;
  theme: ThemeMode;
  dailyGoal: number;
  uiLocale: LocaleId;
  /** Language for UI strings. null means "auto" (follows browser). */
  uiLanguage: SupportedUiLang | null;
  /** The user's native / strongest language — the side translations resolve
      to. Always one of {@link LanguageId}; can't be empty. */
  primaryLang: LanguageId;
  /** Foreign languages the user is studying. Does NOT include
      {@link primaryLang} — if
      a primary is also added here it's filtered out on read. */
  learningLangs: LanguageId[];
  /** Derived from {@link primaryLang} + {@link learningLangs}. Persisted
      for legacy consumers and for read-once selectors that don't want to
      recompute. Always equals `[primary, ...learning.filter(l => l !== primary)]`. */
  activeLangs: LanguageId[];
  /** Optional subset of `activeLangs`-derived directions the user actually
      wants to drill. When undefined or empty, every direction is used.
      Stored as raw "from>to" strings so the Direction type stays in db.ts. */
  activeDirections?: string[];
  /** Opt-in for the bot's daily "word of the day" push (profiles.word_of_day_opt_in,
      see supabase/functions/word-of-day). Undefined/false means opted out —
      it stacks on top of the main due-reminder, so it defaults off. Only
      meaningful once `cloudLinked` and the account has a telegram_id. */
  wordOfDayOptIn?: boolean;
};

/** Compose the canonical activeLangs list from primary + learning. */
function composeActiveLangs(
  primary: LanguageId,
  learning: LanguageId[],
): LanguageId[] {
  const out: LanguageId[] = [primary];
  for (const l of learning) {
    if (l !== primary && !out.includes(l)) out.push(l);
  }
  return out;
}

export type ActivityEntry = {
  date: string; // YYYY-MM-DD (local timezone)
  reviewed: number;
  correct: number;
};

/** Only the parts of AppState that still live in localStorage. Cards and
    decks are now sourced from Dexie via useLiveQuery. */
export type MetaState = {
  schemaVersion: number;
  profile: UserProfile;
  settings: UserSettings;
  activity: ActivityEntry[];
};

type AppState = MetaState & {
  decks: Deck[];
  cards: Card[];
  bootDone: boolean;
};

function todayKey(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

export type NewDeckInput = {
  sourceLang?: LanguageId;
  targetLang?: LanguageId;
  classification?: Classification;
};

export type NewCardInput = {
  entityType?: EntityTypeId;
  frequency?: number;
  classification?: Classification;
  /** Explicit language direction for this card, overriding the owning
      deck's sourceLang/targetLang — used by quickAddWord, where a single
      "My Words" deck can hold cards entered in either direction. */
  direction?: { from: LanguageId; to: LanguageId };
};

type AppContextType = {
  state: AppState;
  addDeck: (name: string, extras?: NewDeckInput) => Promise<string>;
  updateDeck: (
    id: string,
    patch: Partial<Omit<Deck, "id" | "createdAt">>,
  ) => Promise<void>;
  deleteDeck: (id: string) => Promise<void>;
  addCard: (
    deckId: string,
    front: string,
    back: string,
    extras?: NewCardInput,
  ) => Promise<void>;
  /** Add a word to the lazily-created "My Words" deck for the user's active
      language pair (primaryLang + first learningLang), creating that deck
      on first use. `reversed` swaps which of the two languages `front`/
      `back` are in — the pair itself (and so the deck) stays the same. */
  quickAddWord: (
    front: string,
    back: string,
    reversed?: boolean,
  ) => Promise<void>;
  updateCard: (
    id: string,
    patch: Partial<Omit<Card, "id" | "deckId">>,
  ) => Promise<void>;
  deleteCard: (id: string) => Promise<void>;
  updateCardProgress: (cardId: string, quality: number) => Promise<void>;
  gradeConceptProgress: (
    conceptId: string,
    direction: string,
    quality: number,
  ) => Promise<void>;
  installStarterDeck: (starter: StarterDeck) => Promise<string>;
  /** Materialize reverse-direction CardStates for every card in a deck that
      doesn't have one yet. Returns the number of new states created. */
  addReverseDirection: (deckId: string) => Promise<number>;
  updateSettings: (settings: Partial<UserSettings>) => void;
  updateProfile: (profile: Partial<UserProfile>) => void;
  resetData: () => Promise<void>;
};

const META_KEY = "lexibit-meta";
export const META_SCHEMA_VERSION = 4;
/** Persistent (survives a closed/reopened, still-incomplete onboarding) flag:
    set the moment the user explicitly picks a language in Onboarding, so its
    auto-detect effects never overwrite that choice on a later mount. */
export const LANG_TOUCHED_KEY = "lexibit-lang-touched";
const UND = "und" as LanguageId;

const initialMeta: MetaState = {
  schemaVersion: META_SCHEMA_VERSION,
  profile: { name: "", joinedAt: Date.now() },
  settings: {
    role: "student",
    cloudLinked: false,
    onboardingCompleted: false,
    theme: "system",
    dailyGoal: 10,
    // Default from the browser (same source as uiLanguage's fallback below)
    // so first-run screens like onboarding — where the language switcher
    // isn't reachable yet — show UI chrome and content-locale strings in
    // the same language instead of drifting apart. Once onboarding is done,
    // AppLayout's locale switcher lets the two diverge intentionally (e.g.
    // English UI with French-named languages).
    uiLocale: detectBrowserLang(),
    uiLanguage: null,
    primaryLang: "ru",
    learningLangs: ["en"],
    activeLangs: ["ru", "en"],
  },
  activity: [],
};

// Exported for migration regression tests; AppProvider remains this module's component export.
// eslint-disable-next-line react-refresh/only-export-components
export function loadMeta(): MetaState {
  // Prefer the new meta key; fall back to the legacy `lexibit-data` blob
  // (which used to hold settings/profile/activity alongside cards/decks).
  // Without this, existing users would land on /onboarding after the schema
  // switch as if they were brand new.
  let raw = localStorage.getItem(META_KEY);
  if (!raw) {
    const legacy =
      localStorage.getItem("lexibit-data") ??
      localStorage.getItem("exibit-data");
    if (legacy) raw = legacy;
  }
  if (!raw) return initialMeta;
  try {
    return migrateStoredMeta(JSON.parse(raw) as unknown);
  } catch {
    return initialMeta;
  }
}

// Exported for pure migration regression tests and future version steps.
// eslint-disable-next-line react-refresh/only-export-components
export function migrateStoredMeta(stored: unknown): MetaState {
  return migrateStoredMetaState(stored, initialMeta);
}

const AppContext = createContext<AppContextType | undefined>(undefined);

// ---------- helpers: Dexie shape → legacy Card shape ----------

type FormLookup = Map<string, string[]>; // formKey → texts

function buildCard(
  state: CardState,
  concept: Concept | undefined,
  forms: FormLookup,
): Card | null {
  const [from, to] = parseDirection(state.direction);
  const fronts = forms.get(formKey(state.conceptId, from));
  const backs = forms.get(formKey(state.conceptId, to));
  if (!fronts?.length || !backs?.length) return null;
  return {
    id: state.key,
    deckId: state.deckId ?? "",
    sourceLang: from,
    targetLang: to,
    front: fronts[0],
    frontAliases: fronts.slice(1),
    back: backs[0],
    backAliases: backs.slice(1),
    nextReview: state.nextReview,
    interval: state.interval,
    repetition: state.repetition,
    efactor: state.efactor,
    entityType: concept?.entityType,
    frequency: concept?.frequency,
    classification: concept
      ? {
          topic: concept.topic,
          ageGroup: concept.ageGroup,
          level: concept.level,
        }
      : undefined,
  };
}

async function materializeCards(states: CardState[]): Promise<Card[]> {
  if (states.length === 0) return [];
  const conceptIds = [...new Set(states.map((s) => s.conceptId))];
  const [forms, concepts] = await Promise.all([
    db.conceptForms.where("conceptId").anyOf(conceptIds).toArray(),
    db.concepts.where("id").anyOf(conceptIds).toArray(),
  ]);
  const formLookup: FormLookup = new Map();
  for (const f of forms) formLookup.set(f.key, f.texts);
  const conceptById = new Map(concepts.map((c) => [c.id, c]));
  const out: Card[] = [];
  for (const s of states) {
    const card = buildCard(s, conceptById.get(s.conceptId), formLookup);
    if (card) out.push(card);
  }
  return out;
}

function deckToLegacy(d: DeckRow): Deck {
  const classification: Classification | undefined =
    d.topic || d.ageGroup || d.level
      ? {
          ...(d.topic ? { topic: d.topic } : {}),
          ...(d.ageGroup ? { ageGroup: d.ageGroup } : {}),
          ...(d.level ? { level: d.level } : {}),
        }
      : undefined;
  return {
    id: d.id,
    name: d.name,
    createdAt: d.createdAt,
    sourceLang: d.sourceLang,
    targetLang: d.targetLang,
    classification,
  };
}

// ---------- seed demo deck on first run ----------

/** Exported for testing — the boot effect below is the only real caller. */
type DemoSeedSettings = Pick<
  UserSettings,
  "onboardingCompleted" | "primaryLang" | "learningLangs"
>;

export async function seedDemoIfEmpty(
  settings: DemoSeedSettings,
): Promise<void> {
  // Onboarding's own pack-install step is how new users get their first
  // content — seeding a demo deck before (or regardless of) that finishes
  // would plant an unrelated deck the user never chose. Only offer the demo
  // as a fallback once onboarding is done and they still have nothing.
  if (!settings.onboardingCompleted) return;
  const seeded = await db.kv.get(DEMO_SEEDED_KEY);
  if (seeded) return;
  // Check concepts (not just `decks`) so this also skips users who installed
  // onboarding packs — those don't create a `decks` row, only concepts.
  const conceptCount = await db.concepts.count();
  if (conceptCount > 0) {
    await db.kv.put({
      id: DEMO_SEEDED_KEY,
      value: { at: Date.now(), skipped: "already-have-content" },
    });
    return;
  }
  // This legacy demo is authored strictly as Spanish → English. Injecting it
  // into any other language profile makes an empty returning account look as
  // if it contains user-selected material. New users choose a relevant pack
  // during onboarding; the legacy fallback is therefore eligible only for an
  // exact matching profile.
  const demoMatchesProfile =
    settings.primaryLang === "es" && settings.learningLangs.includes("en");
  if (!demoMatchesProfile) {
    await db.kv.put({
      id: DEMO_SEEDED_KEY,
      value: { at: Date.now(), skipped: "language-mismatch" },
    });
    return;
  }
  const deckId = "demo-deck-1";
  const now = Date.now();
  const samples: {
    id: string;
    front: string;
    back: string;
    entityType: EntityTypeId;
  }[] = [
    { id: "demo-card-1", front: "Hola", back: "Hello", entityType: "word" },
    { id: "demo-card-2", front: "Gato", back: "Cat", entityType: "word" },
    { id: "demo-card-3", front: "Perro", back: "Dog", entityType: "word" },
    {
      id: "demo-card-4",
      front: "Gracias",
      back: "Thank you",
      entityType: "phrase",
    },
  ];
  const concepts: Concept[] = samples.map((s) => ({
    id: `user:${s.id}`,
    entityType: s.entityType,
    topic: "basic",
    ageGroup: "adult",
    level: "A1",
    packId: `user:${deckId}`,
  }));
  const forms: ConceptForm[] = samples.flatMap((s) => [
    {
      key: formKey(`user:${s.id}`, "es"),
      conceptId: `user:${s.id}`,
      lang: "es",
      texts: [s.front],
    },
    {
      key: formKey(`user:${s.id}`, "en"),
      conceptId: `user:${s.id}`,
      lang: "en",
      texts: [s.back],
    },
  ]);
  const direction = makeDirection("es", "en");
  const states: CardState[] = samples.map((s) => ({
    key: cardStateKey(`user:${s.id}`, direction),
    conceptId: `user:${s.id}`,
    direction,
    deckId,
    nextReview: 0,
    interval: 0,
    repetition: 0,
    efactor: 2.5,
  }));
  await db.transaction(
    "rw",
    [db.decks, db.concepts, db.conceptForms, db.cardStates, db.kv],
    async () => {
      await db.decks.put({
        id: deckId,
        name: "Spanish Basics",
        createdAt: now,
        sourceLang: "es",
        targetLang: "en",
        topic: "basic",
        ageGroup: "adult",
        level: "A1",
      });
      await db.concepts.bulkPut(concepts);
      await db.conceptForms.bulkPut(forms);
      await db.cardStates.bulkPut(states);
      await db.kv.put({ id: DEMO_SEEDED_KEY, value: { at: Date.now() } });
    },
  );
}

// ---------- deletions ----------

/**
 * Drop the given user concepts and their forms — but only the ones no
 * CardState references any more. {@link AppProvider}'s `addCard` deliberately
 * reuses an existing concept when both surface forms match, so a single
 * concept can back several cards (the reverse direction, the same word filed
 * in another deck). Deleting it wholesale would make those cards silently
 * vanish and leave their CardStates orphaned forever.
 *
 * Call inside a rw transaction, once the CardStates being removed are gone.
 */
async function deleteUnreferencedUserConcepts(
  conceptIds: string[],
): Promise<{ conceptIds: string[]; formKeys: string[] }> {
  const removable: string[] = [];
  for (const conceptId of new Set(conceptIds)) {
    // Pack concepts stay — other directions / packs still reference them.
    if (!conceptId.startsWith("user:")) continue;
    const referencing = await db.cardStates
      .where("conceptId")
      .equals(conceptId)
      .count();
    if (referencing === 0) removable.push(conceptId);
  }
  if (removable.length === 0) return { conceptIds: [], formKeys: [] };
  const forms = await db.conceptForms
    .where("conceptId")
    .anyOf(removable)
    .toArray();
  const formKeys = forms.map((f) => f.key);
  await db.concepts.bulkDelete(removable);
  await db.conceptForms.bulkDelete(formKeys);
  return { conceptIds: removable, formKeys };
}

/** Exported for testing — {@link AppProvider}'s `deleteDeck` is the only real caller. */
// eslint-disable-next-line react-refresh/only-export-components
export async function deleteDeckData(id: string): Promise<void> {
  await db.transaction(
    "rw",
    [db.decks, db.cardStates, db.concepts, db.conceptForms, db.outbox],
    async () => {
      const states = await db.cardStates.where("deckId").equals(id).toArray();
      const deletedStateKeys = states.map((s) => s.key);
      await db.cardStates.bulkDelete(deletedStateKeys);
      const deleted = await deleteUnreferencedUserConcepts(
        states.map((s) => s.conceptId),
      );
      await db.decks.delete(id);
      // Outbox writes are atomic with the data deletes
      await enqueueInTx("decks", "delete", id);
      for (const key of deletedStateKeys)
        await enqueueInTx("cardStates", "delete", key);
      for (const cid of deleted.conceptIds)
        await enqueueInTx("concepts", "delete", cid);
      for (const fkey of deleted.formKeys)
        await enqueueInTx("conceptForms", "delete", fkey);
    },
  );
  scheduleFlush();
}

/** Exported for testing — {@link AppProvider}'s `deleteCard` is the only real caller. */
// eslint-disable-next-line react-refresh/only-export-components
export async function deleteCardData(id: string): Promise<void> {
  const state = await db.cardStates.get(id);
  if (!state) return;
  await db.transaction(
    "rw",
    [db.cardStates, db.concepts, db.conceptForms, db.outbox],
    async () => {
      await db.cardStates.delete(id);
      const deleted = await deleteUnreferencedUserConcepts([state.conceptId]);
      // Outbox writes are atomic with the data deletes
      await enqueueInTx("cardStates", "delete", id);
      for (const cid of deleted.conceptIds)
        await enqueueInTx("concepts", "delete", cid);
      for (const fkey of deleted.formKeys)
        await enqueueInTx("conceptForms", "delete", fkey);
    },
  );
  scheduleFlush();
}

// ---------- provider ----------

export function AppProvider({ children }: { children: ReactNode }) {
  const [meta, setMeta] = useState<MetaState>(loadMeta);
  const [bootDone, setBootDone] = useState(false);

  // Boot: migrate localStorage cards/decks into Dexie (if any), then seed
  // the demo deck if everything is still empty. `bootDone` gates the live
  // queries so we don't briefly render "no decks" before the seed lands.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        await migrateFromLocalStorageIfNeeded();
        // Packs installed before they belonged to a dictionary would otherwise
        // stay invisible on a shelf that now only shows decks.
        await adoptOrphanPacks();
        await seedDemoIfEmpty(meta.settings);
      } finally {
        if (!cancelled) setBootDone(true);
      }
    })();
    return () => {
      cancelled = true;
    };
    // Intentionally run once on mount with whatever `onboardingCompleted` was
    // at load time — this is a one-shot boot step, not a live subscription.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Persist meta (settings/profile/activity) — small synchronous blob.
  useEffect(() => {
    localStorage.setItem(META_KEY, JSON.stringify(meta));
  }, [meta]);

  // Backup reminder: once after boot, for users without cloud sync.
  useEffect(() => {
    if (!bootDone) return;
    (async () => {
      try {
        const [
          lastExportRow,
          lastReminderRow,
          cardStateCount,
          deckCount,
          oldestDeck,
        ] = await Promise.all([
          db.kv.get(KV_LAST_EXPORT_AT),
          db.kv.get(KV_LAST_REMINDER_AT),
          db.cardStates.count(),
          db.decks.count(),
          db.decks.orderBy("createdAt").first(),
        ]);

        const lastExportAt =
          typeof lastExportRow?.value === "number" ? lastExportRow.value : null;
        const lastReminderAt =
          typeof lastReminderRow?.value === "number"
            ? lastReminderRow.value
            : null;

        const remind = shouldRemindBackup({
          lastExportAt,
          lastReminderAt,
          cloudLinked: meta.settings.cloudLinked,
          cardStateCount,
          deckCount,
          oldestDeckCreatedAt: oldestDeck?.createdAt ?? null,
          now: Date.now(),
        });

        if (!remind) return;

        const now = Date.now();
        await db.kv.put({ id: KV_LAST_REMINDER_AT, value: now });

        toast.warning(i18n.t("backup.reminderTitle"), {
          description: i18n.t("backup.reminderDesc"),
          duration: 12000,
          action: {
            label: i18n.t("backup.reminderAction"),
            onClick: () => {
              window.location.href = "/settings";
            },
          },
        });
      } catch {
        // Never block the app on a reminder failure
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bootDone]);

  // Theme on <html>.
  useEffect(() => {
    const root = document.documentElement;
    const media = window.matchMedia("(prefers-color-scheme: dark)");
    const applyTheme = () => {
      root.classList.toggle(
        "dark",
        resolvesToDark(meta.settings.theme, media.matches),
      );
      root.dataset.theme = meta.settings.theme;
    };
    applyTheme();
    media.addEventListener("change", applyTheme);
    return () => media.removeEventListener("change", applyTheme);
  }, [meta.settings.theme]);

  // Sync UI language with i18next and update <html lang>.
  useEffect(() => {
    const lang =
      meta.settings.uiLanguage ??
      detectBrowserLang(getPlatformAdapter().getLanguage());
    let active = true;
    document.documentElement.lang = lang;
    void i18n.changeLanguage(lang).then(() => {
      if (!active) return;
      document.title = i18n.t("meta.title");
      document
        .querySelector('meta[name="description"]')
        ?.setAttribute("content", i18n.t("meta.description"));
      document
        .querySelector('meta[property="og:title"]')
        ?.setAttribute("content", i18n.t("meta.title"));
      document
        .querySelector('meta[property="og:description"]')
        ?.setAttribute("content", i18n.t("meta.description"));
      document
        .querySelector('link[rel="manifest"]')
        ?.setAttribute(
          "href",
          lang === "ko"
            ? "/manifest.ko-KR.webmanifest"
            : lang === "ru"
              ? "/manifest.ru-RU.webmanifest"
              : "/manifest.webmanifest",
        );
    });
    return () => {
      active = false;
    };
  }, [meta.settings.uiLanguage]);

  // Live decks: small table (dozens), no filter needed.
  const deckRows = useLiveQuery<DeckRow[]>(
    () =>
      bootDone
        ? db.decks.orderBy("createdAt").toArray()
        : Promise.resolve([] as DeckRow[]),
    [bootDone],
  );

  // Live cards: all cardStates that belong to a user deck, materialized in
  // the same live query so one grade re-renders consumers once instead of
  // twice. `orderBy("deckId")` walks the deckId index, which also does the
  // filtering for free: pack-only drill states (deckId undefined) are not in
  // the index, so they stay out of the legacy Card[] and don't pollute
  // Decks/DeckView/Dashboard counts.
  const cardRows = useLiveQuery<Card[]>(
    async () =>
      bootDone
        ? materializeCards(await db.cardStates.orderBy("deckId").toArray())
        : [],
    [bootDone],
  );

  const state: AppState = useMemo(
    () => ({
      ...meta,
      decks: (deckRows ?? []).map(deckToLegacy),
      cards: cardRows ?? [],
      bootDone,
    }),
    [meta, deckRows, cardRows, bootDone],
  );

  // ---------- mutators ----------

  const addDeck = useCallback(
    async (name: string, extras: NewDeckInput = {}): Promise<string> => {
      const id = crypto.randomUUID();
      await db.decks.put({
        id,
        name,
        createdAt: Date.now(),
        sourceLang: extras.sourceLang,
        targetLang: extras.targetLang,
        topic: extras.classification?.topic,
        ageGroup: extras.classification?.ageGroup,
        level: extras.classification?.level,
      });
      enqueue("decks", "upsert", id).catch(() => {});
      return id;
    },
    [],
  );

  const updateDeck = useCallback(
    async (
      id: string,
      patch: Partial<Omit<Deck, "id" | "createdAt">>,
    ): Promise<void> => {
      const existing = await db.decks.get(id);
      if (!existing) return;
      await db.decks.put({
        ...existing,
        name: patch.name ?? existing.name,
        sourceLang: patch.sourceLang ?? existing.sourceLang,
        targetLang: patch.targetLang ?? existing.targetLang,
        topic: patch.classification?.topic ?? existing.topic,
        ageGroup: patch.classification?.ageGroup ?? existing.ageGroup,
        level: patch.classification?.level ?? existing.level,
      });
      enqueue("decks", "upsert", id).catch(() => {});
    },
    [],
  );

  const addCard = useCallback(
    async (
      deckId: string,
      front: string,
      back: string,
      extras: NewCardInput = {},
    ): Promise<void> => {
      const deck = await db.decks.get(deckId);
      const fromLang = (extras.direction?.from ??
        deck?.sourceLang ??
        UND) as LanguageId;
      const toLang = (extras.direction?.to ??
        deck?.targetLang ??
        UND) as LanguageId;
      const direction = makeDirection(fromLang, toLang);

      const txResult = await db.transaction(
        "rw",
        [db.concepts, db.conceptForms, db.cardStates],
        async () => {
          // Try to dedupe against an existing concept that already carries BOTH
          // forms (front in fromLang AND back in toLang). Sharing concepts means
          // SRS progress is reused across decks/packs for the same word pair.
          const candidates = await db.conceptForms
            .where("[conceptId+lang]")
            .between([Dexie.minKey, fromLang], [Dexie.maxKey, fromLang])
            .filter((f) => f.texts.includes(front))
            .toArray();
          let conceptId: string | null = null;
          for (const candidate of candidates) {
            const reverseForm = await db.conceptForms.get(
              formKey(candidate.conceptId, toLang),
            );
            if (reverseForm?.texts.includes(back)) {
              conceptId = candidate.conceptId;
              break;
            }
          }

          let isNewConcept = false;
          if (!conceptId) {
            isNewConcept = true;
            conceptId = `user:${crypto.randomUUID()}`;
            const concept: Concept = {
              id: conceptId,
              entityType: extras.entityType ?? "word",
              frequency: extras.frequency,
              topic: extras.classification?.topic ?? deck?.topic,
              ageGroup: extras.classification?.ageGroup ?? deck?.ageGroup,
              level: extras.classification?.level ?? deck?.level,
              packId: `user:${deckId}`,
            };
            await db.concepts.put(concept);
            await db.conceptForms.bulkPut([
              {
                key: formKey(conceptId, fromLang),
                conceptId,
                lang: fromLang,
                texts: [front],
              },
              {
                key: formKey(conceptId, toLang),
                conceptId,
                lang: toLang,
                texts: [back],
              },
            ]);
          }

          // CardState is keyed by (concept, direction) — if the user already had
          // this exact card in another deck, keep the existing SRS state and just
          // re-point its deckId. Otherwise create a fresh row.
          const stateKey = cardStateKey(conceptId, direction);
          const existing = await db.cardStates.get(stateKey);
          if (existing) {
            await db.cardStates.put({ ...existing, deckId });
          } else {
            await db.cardStates.put({
              key: stateKey,
              conceptId,
              direction,
              deckId,
              nextReview: Date.now(),
              interval: 0,
              repetition: 0,
              efactor: 2.5,
            });
          }
          return { conceptId, isNewConcept, stateKey };
        },
      );
      if (txResult.isNewConcept) {
        enqueue("concepts", "upsert", txResult.conceptId).catch(() => {});
        enqueue(
          "conceptForms",
          "upsert",
          formKey(txResult.conceptId, fromLang),
        ).catch(() => {});
        enqueue(
          "conceptForms",
          "upsert",
          formKey(txResult.conceptId, toLang),
        ).catch(() => {});
      }
      enqueue("cardStates", "upsert", txResult.stateKey).catch(() => {});
    },
    [],
  );

  const quickAddWord = useCallback(
    async (front: string, back: string, reversed = false): Promise<void> => {
      const { primaryLang, learningLangs } = meta.settings;
      const learning = learningLangs[0];
      // UI callers (QuickAddWordDialog) only render once activeLangs.length >= 2,
      // so this is a defensive guard, not the primary gate.
      if (!learning) return;
      const { id: deckId, created } = await ensureDefaultWordsDeck(
        primaryLang,
        learning,
        i18n.t("myWords.defaultDeckName"),
      );
      if (created) enqueue("decks", "upsert", deckId).catch(() => {});
      const fromLang = reversed ? primaryLang : learning;
      const toLang = reversed ? learning : primaryLang;
      await addCard(deckId, front, back, {
        direction: { from: fromLang, to: toLang },
      });
    },
    [addCard, meta.settings],
  );

  const updateCard = useCallback(
    async (
      id: string,
      patch: Partial<Omit<Card, "id" | "deckId">>,
    ): Promise<void> => {
      const state = await db.cardStates.get(id);
      if (!state) return;
      const [from, to] = parseDirection(state.direction);
      await db.transaction(
        "rw",
        [db.concepts, db.conceptForms, db.cardStates],
        async () => {
          // SRS-side patches go to cardStates directly.
          const next: CardState = {
            ...state,
            nextReview: patch.nextReview ?? state.nextReview,
            interval: patch.interval ?? state.interval,
            repetition: patch.repetition ?? state.repetition,
            efactor: patch.efactor ?? state.efactor,
          };
          await db.cardStates.put(next);
          // Text patches rewrite the conceptForm row for that lang.
          if (patch.front !== undefined) {
            await db.conceptForms.put({
              key: formKey(state.conceptId, from),
              conceptId: state.conceptId,
              lang: from,
              texts: [patch.front],
            });
          }
          if (patch.back !== undefined) {
            await db.conceptForms.put({
              key: formKey(state.conceptId, to),
              conceptId: state.conceptId,
              lang: to,
              texts: [patch.back],
            });
          }
          // entityType / frequency / classification live on the concept.
          if (
            patch.entityType !== undefined ||
            patch.frequency !== undefined ||
            patch.classification !== undefined
          ) {
            const concept = await db.concepts.get(state.conceptId);
            if (concept) {
              await db.concepts.put({
                ...concept,
                entityType: patch.entityType ?? concept.entityType,
                frequency: patch.frequency ?? concept.frequency,
                topic: patch.classification?.topic ?? concept.topic,
                ageGroup: patch.classification?.ageGroup ?? concept.ageGroup,
                level: patch.classification?.level ?? concept.level,
              });
            }
          }
        },
      );
      enqueue("cardStates", "upsert", id).catch(() => {});
      if (patch.front !== undefined)
        enqueue("conceptForms", "upsert", formKey(state.conceptId, from)).catch(
          () => {},
        );
      if (patch.back !== undefined)
        enqueue("conceptForms", "upsert", formKey(state.conceptId, to)).catch(
          () => {},
        );
      if (
        patch.entityType !== undefined ||
        patch.frequency !== undefined ||
        patch.classification !== undefined
      ) {
        enqueue("concepts", "upsert", state.conceptId).catch(() => {});
      }
    },
    [],
  );

  const installStarterDeck = useCallback(
    async (starter: StarterDeck): Promise<string> => {
      const starterCards = await loadStarterDeckCards(starter);
      const newDeckId = crypto.randomUUID();
      const now = Date.now();
      const fromLang = starter.sourceLang;
      const toLang = starter.targetLang;
      const direction = makeDirection(fromLang, toLang);

      const concepts: Concept[] = [];
      const forms: ConceptForm[] = [];
      const states: CardState[] = [];
      for (const c of starterCards) {
        const conceptId = `user:${crypto.randomUUID()}`;
        concepts.push({
          id: conceptId,
          entityType: c.entityType ?? "word",
          topic: c.classification?.topic ?? starter.topic,
          ageGroup: c.classification?.ageGroup ?? starter.ageGroup,
          level: c.classification?.level ?? starter.level,
          packId: `user:${newDeckId}`,
        });
        forms.push(
          {
            key: formKey(conceptId, fromLang),
            conceptId,
            lang: fromLang,
            texts: [c.front, ...(c.frontAliases ?? [])],
          },
          {
            key: formKey(conceptId, toLang),
            conceptId,
            lang: toLang,
            texts: [c.back, ...(c.backAliases ?? [])],
          },
        );
        states.push({
          key: cardStateKey(conceptId, direction),
          conceptId,
          direction,
          deckId: newDeckId,
          nextReview: now,
          interval: 0,
          repetition: 0,
          efactor: 2.5,
        });
      }

      await db.transaction(
        "rw",
        [db.decks, db.concepts, db.conceptForms, db.cardStates, db.kv],
        async () => {
          await db.decks.put({
            id: newDeckId,
            name: localizedPackName(starter, detectBrowserLang(i18n.resolvedLanguage ?? i18n.language)),
            createdAt: now,
            sourceLang: fromLang,
            targetLang: toLang,
            topic: starter.topic,
            ageGroup: starter.ageGroup,
            level: starter.level,
          });
          await db.concepts.bulkPut(concepts);
          await db.conceptForms.bulkPut(forms);
          await db.cardStates.bulkPut(states);
          if (starter.catalogProvenance) {
            await db.kv.put({
              id: starter.catalogProvenance.key,
              value: {
                deckId: newDeckId,
                packVersion: starter.catalogProvenance.packVersion,
                cardCount: starter.catalogProvenance.cardCount,
              },
            });
          }
        },
      );
      enqueue("decks", "upsert", newDeckId).catch(() => {});
      for (const c of concepts)
        enqueue("concepts", "upsert", c.id).catch(() => {});
      for (const f of forms)
        enqueue("conceptForms", "upsert", f.key).catch(() => {});
      for (const s of states)
        enqueue("cardStates", "upsert", s.key).catch(() => {});
      return newDeckId;
    },
    [],
  );

  const addReverseDirection = useCallback(
    async (deckId: string): Promise<number> => {
      const deck = await db.decks.get(deckId);
      if (!deck?.sourceLang || !deck?.targetLang) return 0;
      const reverse = makeDirection(deck.targetLang, deck.sourceLang);
      const forward = makeDirection(deck.sourceLang, deck.targetLang);
      const states = await db.cardStates
        .where("deckId")
        .equals(deckId)
        .toArray();
      const now = Date.now();
      const created: CardState[] = [];
      for (const s of states) {
        if (s.direction !== forward) continue;
        const key = cardStateKey(s.conceptId, reverse);
        const exists = await db.cardStates.get(key);
        if (exists) continue;
        created.push({
          key,
          conceptId: s.conceptId,
          direction: reverse,
          deckId,
          nextReview: now,
          interval: 0,
          repetition: 0,
          efactor: 2.5,
        });
      }
      if (created.length > 0) {
        await db.cardStates.bulkPut(created);
        for (const s of created)
          enqueue("cardStates", "upsert", s.key).catch(() => {});
      }
      return created.length;
    },
    [],
  );

  const bumpActivity = useCallback(async (quality: number) => {
    const today = todayKey();
    const isCorrect = quality >= 3;
    setMeta((prev) => {
      const existing = prev.activity.find((a) => a.date === today);
      const activity = existing
        ? prev.activity.map((a) =>
            a.date === today
              ? {
                  ...a,
                  reviewed: a.reviewed + 1,
                  correct: a.correct + (isCorrect ? 1 : 0),
                }
              : a,
          )
        : [
            ...prev.activity,
            { date: today, reviewed: 1, correct: isCorrect ? 1 : 0 },
          ];
      return { ...prev, activity };
    });

  }, []);

  const updateCardProgress = useCallback(
    async (cardId: string, quality: number): Promise<void> => {
      const state = await db.cardStates.get(cardId);
      if (!state) throw new Error(`Card state not found: ${cardId}`);
      await gradeCardState(
        state.conceptId,
        state.direction as Direction,
        quality,
      );
      await enqueue(
        "cardStates",
        "upsert",
        cardStateKey(state.conceptId, state.direction as Direction),
      );
      await bumpActivity(quality);
    },
    [bumpActivity],
  );

  const gradeConceptProgress = useCallback(
    async (
      conceptId: string,
      direction: string,
      quality: number,
    ): Promise<void> => {
      await gradeCardState(conceptId, direction as Direction, quality);
      enqueue(
        "cardStates",
        "upsert",
        cardStateKey(conceptId, direction as Direction),
      ).catch(() => {});
      await bumpActivity(quality);
    },
    [bumpActivity],
  );

  const updateSettings = useCallback((newSettings: Partial<UserSettings>) => {
    setMeta((prev) => {
      const merged = { ...prev.settings, ...newSettings };
      // Normalize learning languages: dedupe and drop the primary if it
      // slipped in. Do not cap a saved polyglot profile during an unrelated
      // settings change; every legacy language remains manageable in UI.
      if (newSettings.learningLangs || newSettings.primaryLang) {
        const primary = merged.primaryLang;
        const unique: LanguageId[] = [];
        for (const l of merged.learningLangs ?? []) {
          if (l === primary) continue;
          if (unique.includes(l)) continue;
          unique.push(l);
        }
        merged.learningLangs = unique;
      }
      // Legacy callers can still set `activeLangs` directly — interpret it
      // as "first one is primary, the rest are learning" so old code paths
      // keep working until rewritten.
      if (
        newSettings.activeLangs &&
        !newSettings.primaryLang &&
        !newSettings.learningLangs
      ) {
        const [first, ...rest] = newSettings.activeLangs;
        if (first) {
          merged.primaryLang = first;
          merged.learningLangs = rest
            .filter((l, i, arr) => l !== first && arr.indexOf(l) === i);
        }
      }
      merged.activeLangs = composeActiveLangs(
        merged.primaryLang,
        merged.learningLangs,
      );
      if (
        newSettings.primaryLang ||
        newSettings.learningLangs ||
        newSettings.activeLangs
      ) {
        const validDirections = new Set<string>();
        for (const source of merged.activeLangs) {
          for (const target of merged.activeLangs) {
            if (source !== target) validDirections.add(`${source}>${target}`);
          }
        }
        const normalizedDirections = (merged.activeDirections ?? []).filter(
          (direction) => validDirections.has(direction),
        );
        // `undefined` is the compact representation for "all directions".
        merged.activeDirections =
          normalizedDirections.length === 0 ||
          normalizedDirections.length === validDirections.size
            ? undefined
            : normalizedDirections;
      }
      return { ...prev, settings: merged };
    });
  }, []);

  const updateProfile = useCallback((newProfile: Partial<UserProfile>) => {
    setMeta((prev) => ({
      ...prev,
      profile: { ...prev.profile, ...newProfile },
    }));
  }, []);

  const resetData = useCallback(async (): Promise<void> => {
    await quiesceSync();
    // Sign out of Supabase first when cloud-linked — otherwise the session
    // persists in its own localStorage key (outside our control) and the
    // next boot's AuthBridge would re-detect it, clear
    // `sync:initialDone:<userId>` having just been wiped from `db.kv`, and
    // pull the "deleted" data straight back down from the cloud.
    if (meta.settings.cloudLinked) {
      await supabase?.auth.signOut().catch(() => {});
    }
    await db.transaction(
      "rw",
      [
        db.decks,
        db.concepts,
        db.conceptForms,
        db.cardStates,
        db.activity,
        db.reviewEvents,
        db.kv,
        db.outbox,
      ],
      async () => {
        await Promise.all([
          db.decks.clear(),
          db.concepts.clear(),
          db.conceptForms.clear(),
          db.cardStates.clear(),
          db.activity.clear(),
          db.reviewEvents.clear(),
          db.kv.clear(),
          db.outbox.clear(),
        ]);
      },
    );
    localStorage.removeItem(META_KEY);
    localStorage.removeItem("lexibit-data");
    localStorage.removeItem("exibit-data");
    localStorage.removeItem(LANG_TOUCHED_KEY);
    localStorage.removeItem("lexibit.analytics.deviceId");
    localStorage.removeItem("lexibit.analytics.consent");
    localStorage.removeItem("lexibit.analytics.optOut");
    localStorage.removeItem("lexibit.analytics.queue");
    localStorage.removeItem("lexibit.analytics.lastPingDay");
    sessionStorage.removeItem("lexibit.analytics.tgLaunchTracked");
    setMeta(initialMeta);
  }, [meta.settings.cloudLinked]);

  // Memoized so the 40-plus context consumers only re-render when `state`
  // actually changes, not on every render of this provider.
  const value: AppContextType = useMemo(
    () => ({
      state,
      addDeck,
      updateDeck,
      deleteDeck: deleteDeckData,
      addCard,
      quickAddWord,
      updateCard,
      deleteCard: deleteCardData,
      updateCardProgress,
      gradeConceptProgress,
      installStarterDeck,
      addReverseDirection,
      updateSettings,
      updateProfile,
      resetData,
    }),
    [
      state,
      addDeck,
      updateDeck,
      addCard,
      quickAddWord,
      updateCard,
      updateCardProgress,
      gradeConceptProgress,
      installStarterDeck,
      addReverseDirection,
      updateSettings,
      updateProfile,
      resetData,
    ],
  );

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}

export function useAppContext() {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error("useAppContext must be used within an AppProvider");
  }
  return context;
}
